import i18n from "@/src/i18n";
import type { ModelError } from "@iot-platform/core";
import {
    FormDataSerializer,
    NonRecuperableModelError,
} from "@iot-platform/core";
import type {
    NotebookMessageCreateBody,
    NotebookMessageCreateBodyContent,
    NotebookMessageDto,
    NotebookMessageFileBody,
    NotebookMessageFileBodyContent,
    NotebookMessagesDto,
    NotebookMessageUpdateBody,
    NotebookMessageUpdateBodyContent,
} from "@notebook/data/dto/notebook-message.dto";
import {
    NotebookMessageDtoSchema,
    NotebookMessagesDtoSchema,
} from "@notebook/data/dto/notebook-message.dto";
import type {
    NotebookMessageCreate,
    NotebookMessageCreateInputProps,
    NotebookMessageFileCreate,
    NotebookMessageUpdate,
    NotebookMessageUpdateInputProps,
} from "@notebook/domain/models/notebook-message.model";
import {
    NotebookMessage,
    NotebookMessageCreateSchema,
    NotebookMessages,
    NotebookMessageUpdateSchema,
} from "@notebook/domain/models/notebook-message.model";
// eslint-disable-next-line boundaries/dependencies
import type { MessageModalFormValuesValidated } from "@notebook/presentation/components/diary/message-modal-form.view";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import type * as z from "zod";

@injectable()
export class NotebookMessageMapper {
    map(
        notebookMessageDto: NotebookMessageDto,
    ): Result<NotebookMessage, ModelError<typeof NotebookMessage>> {
        const notebookMessageDtoParsed =
            NotebookMessageDtoSchema.safeParse(notebookMessageDto);

        if (!notebookMessageDtoParsed.success) {
            console.error(notebookMessageDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return NotebookMessage.createWithRecovery({
            id: notebookMessageDto.id,
            title: notebookMessageDto.title,
            description: notebookMessageDto.description,
            time: notebookMessageDto.time,
            userId: notebookMessageDto.user_id,
            userEmail: notebookMessageDto.user_email,
            files: notebookMessageDto.files
                ? notebookMessageDto.files.map((file) => ({
                      id: file.id,
                      url: file.url,
                  }))
                : null,
        });
    }

    mapList(notebookMessagesDto: NotebookMessagesDto): NotebookMessages {
        const notebookMessagesDtoParsed =
            NotebookMessagesDtoSchema.safeParse(notebookMessagesDto);

        if (!notebookMessagesDtoParsed.success) {
            console.error(notebookMessagesDtoParsed.error.issues);
            return NotebookMessages.empty();
        }

        const notebookMessages = notebookMessagesDto.mapNotNull(
            (notebookMessageDto) =>
                this.map(notebookMessageDto).match(
                    (notebookMessage) => notebookMessage,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping notebook messages:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
        );

        return NotebookMessages.create({
            notebookMessages,
            count: notebookMessages.length,
        }).unwrapOr(NotebookMessages.empty());
    }

    mapFormToCreate(
        notebookMessage: MessageModalFormValuesValidated,
    ): Result<NotebookMessageCreate, z.ZodError<NotebookMessageCreate>> {
        const maybeNotebookMessageCreate =
            NotebookMessageCreateSchema.safeParse({
                title: notebookMessage.title,
                description: notebookMessage.description,
                userId: notebookMessage.userId,
                time: notebookMessage.time ?? undefined,
            } satisfies NotebookMessageCreateInputProps);

        return maybeNotebookMessageCreate.success
            ? ok(maybeNotebookMessageCreate.data)
            : err(maybeNotebookMessageCreate.error);
    }

    mapToCreateBody(
        notebookMessageCreate: NotebookMessageCreate,
    ): NotebookMessageCreateBody {
        const bodyContent: NotebookMessageCreateBodyContent = {
            notebook_message: {
                title: notebookMessageCreate.title,
                description: notebookMessageCreate.description,
                time:
                    notebookMessageCreate.time
                        ?.setLocale(i18n.language)
                        .toISO() ?? undefined,
                user_id: notebookMessageCreate.userId,
            },
        };

        return FormDataSerializer.serialize(bodyContent);
    }

    mapFormToEdit(
        id: number,
        notebookMessage: MessageModalFormValuesValidated,
    ): Result<NotebookMessageUpdate, z.ZodError<NotebookMessageUpdate>> {
        const maybeNotebookMessageUpdate =
            NotebookMessageUpdateSchema.safeParse({
                id,
                title: notebookMessage.title,
                description: notebookMessage.description,
                userId: notebookMessage.userId,
                time: notebookMessage.time ?? undefined,
            } satisfies NotebookMessageUpdateInputProps);

        return maybeNotebookMessageUpdate.success
            ? ok(maybeNotebookMessageUpdate.data)
            : err(maybeNotebookMessageUpdate.error);
    }

    mapToEditBody(
        notebookMessageEdit: NotebookMessageUpdate,
    ): NotebookMessageUpdateBody {
        const bodyContent: NotebookMessageUpdateBodyContent = {
            notebook_message: {
                id: notebookMessageEdit.id,
                title: notebookMessageEdit.title,
                description: notebookMessageEdit.description,
                time:
                    notebookMessageEdit.time
                        ?.setLocale(i18n.language)
                        .toISO() ?? undefined,
                user_id: notebookMessageEdit.userId,
            },
        };

        return FormDataSerializer.serialize(bodyContent);
    }

    mapToCreateFileBody(
        notebookMessageFile: NotebookMessageFileCreate,
    ): NotebookMessageFileBody {
        const bodyContent: NotebookMessageFileBodyContent = {
            notebook_message: {
                files: notebookMessageFile.files,
            },
        };

        return FormDataSerializer.serialize(bodyContent);
    }
}
